<script lang="ts">
    import { onMount } from "svelte";

    import SVGIcon from "$/components/SVGIcon.svelte";

    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import {
        checkAndGenerateSocialMediaURL,
        generateBase64ImageFromUrl,
        toggleSocialMediaPostSaveState,
    } from "$/utils/Utils";
    import { AppStore } from "$/stores/AppStore";
    import { v4 as uuid } from "uuid";
    import {ExtensionStore} from "$/stores/ExtensionStore";

    let saveButton: HTMLButtonElement;
    let observer: MutationObserver;
    let initialLoad = false;
    let buttonColor: string;
    export let editRole: number;

    // function to create the "Save Post" button
    const createSaveButton = (element) => {
        const postSaveButton = saveButton.cloneNode(true) as HTMLButtonElement;

        postSaveButton.classList.remove("cw-save-hide");
        postSaveButton.addEventListener("click", async () => {
            toggleSocialMediaPostSaveState(postSaveButton, true);

            const post = await extractPostData(element);
            let snippet = {
                id: uuid(),
                content: post.postContent ?? "No content",
                nameSuggestion: post.name?.length
                    ? `${post.name} on X`
                    : "X Post",
                comments: [],
                url: checkAndGenerateSocialMediaURL("twitter", post.url),
                createdBy: $TokenStore?.user,
                createdOn: new Date().getTime(),
                author: {
                    name: post.name,
                    username: post.username,
                    url: post.authorUrl,
                    image: post.image,
                },
            };

            ExtensionStore.setOpened(true);

            ActiveTopicStore.createSnippet(snippet);

            // set focus on the newly created snippet
            AppStore.setFocused({ snippetId: snippet.id });

            toggleSocialMediaPostSaveState(postSaveButton, false);
        });

        return postSaveButton;
    };

    const generateXAuthorName = (element: HTMLElement) => {
        return element.querySelector("[data-testid='User-Name']")?.firstChild
            ?.textContent;
    };

    const generateXPostUrl = (element: HTMLElement) => {
        let statusUrl = "";
        // find all a elements and get href
        const aElements = element.querySelectorAll("a");

        // find a element that has /status/ in href
        let href = "";
        aElements.forEach((aElement) => {
            if (aElement.getAttribute("href")?.includes("/status/"))
                href = aElement.getAttribute("href");
        });

        // strip everything after /status/numberid/
        statusUrl =
            href?.split("/status/")[0] +
            "/status/" +
            href?.split("/status/")[1].split("/")[0];
        if (!statusUrl || statusUrl === "")
            throw new Error("Could not find status url");

        // return current url till .com + statusUrl
        // because sometimes location is /home /messages etc...
        return window.location.href.split(".com")[0] + ".com" + statusUrl;
    };

    const generateXPostContent = (element: HTMLElement) => {
        // try to find data-testId="tweetText" and  if exists
        // return get innerHTML
        const tweetTextElement = element.querySelector(
            "[data-testid='tweetText']",
        );
        let postContent = tweetTextElement?.innerHTML;

        // if theres an <img alt=\&quot; in the innerHTML of tweetTextElement
        // remove any img tag and place alt text instead of it as span
        if (postContent?.includes('<img alt="'))
            postContent = postContent.replace(
                /<img alt=\".*?\".*?>/g,
                (match) => {
                    const altText = match.split('alt="')[1].split('"')[0];
                    return `<span>${altText}</span>`;
                },
            );

        // if theres an new line in the innerHTML of tweetTextElement replace it with <br>
        if (postContent?.includes("\n"))
            postContent = postContent.replace(/\n/g, "<br>");
        return postContent;
    };

    const generateXAuthorImage = async (
        element: HTMLElement,
    ): Promise<string | null> => {
        const img = element.querySelector(
            "[data-testid^='UserAvatar-Container'] a img",
        );
        if (!img) return null;

        const src = img.getAttribute("src");
        return src ? await generateBase64ImageFromUrl(src) : null;
    };

    const generateXAuthorUsername = (element: HTMLElement): string | null => {
        const link = element.querySelector(
            "[data-testid^='UserAvatar-Container'] a",
        );
        const href = link?.getAttribute("href");
        return href ? href.split("/")[1] : null;
    };

    const extractPostData = async (element: HTMLElement) => {
        const image = await generateXAuthorImage(element);
        const username = generateXAuthorUsername(element);
        const postContent = generateXPostContent(element);
        const name = generateXAuthorName(element);
        const url = generateXPostUrl(element);
        const authorUrl = username ? `https://x.com/${username}` : null;

        return { postContent, name, image, username, authorUrl, url };
    };

    const addSaveButtonToPosts = () => {
        const postElements = document.querySelectorAll("[data-testid='tweet']");

        postElements.forEach((element: HTMLElement) => {
            if (!element.querySelector(".cw-save-post-btn")) {
                const saveButton = createSaveButton(element);

                // find share button aria-label="Share post" then get his parent parent parent :)
                // and append our button to his parent
                const socialActions = element.querySelector(
                    "[aria-label='Share post']",
                )?.parentElement?.parentElement?.parentElement;
                socialActions?.appendChild(saveButton);
            }
        });
    };

    // function to check the current URL and add Save Post buttons if needed
    const checkAndAddSaveButtons = () => {
        const currentUrl = window.location.href;
        if (
            currentUrl.includes("x.com") ||
            currentUrl.includes("twitter.com")
        ) {
            if (!document.querySelector("[data-testid='tweet']")) {
                setTimeout(() => {
                    addSaveButtonToPosts();
                }, 1000);
            } else {
                addSaveButtonToPosts();
            }
        }

        // continue observing for new posts
        observer.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    };

    // function to remove all "Save Post" buttons
    const removeSaveButtons = () => {
        const saveButtons = document.querySelectorAll(".cw-save-post-btn");
        saveButtons.forEach((button: HTMLElement) => button.remove());

        // stop observing changes in the document body if observer exists
        observer?.disconnect();
    };

    onMount(() => {
        // listener for new elements with the class "css-1dbjc4n.r-18u37iz.r-1wtj0ep.r-1s2bzr4.r-1mdbhws"
        observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" && editRole >= 3) {
                    mutation.addedNodes.forEach((addedNode) => {
                        // if added node contains data-testid="tweet"
                        // add Save Post button
                        if (
                            addedNode instanceof HTMLElement &&
                            addedNode.querySelector("[data-testid='tweet']")
                        ) {
                            addSaveButtonToPosts();
                        }
                    });
                }
            });
        });

        // initial check for posts on page load
        if (editRole >= 3) {
            checkAndAddSaveButtons();
        }
        initialLoad = true;
    });

    // check if dim mode is enabled and set button color accordingly
    $: document
        .querySelector("meta[name='theme-color']")
        ?.getAttribute("content") === "#15202B"
        ? (buttonColor = "#8b98a5")
        : (buttonColor = "#666666");

    // if user has edit role >= 3 and initial load (component mounted), add "Save Post" buttons else remove them
    $: initialLoad && editRole >= 3
        ? checkAndAddSaveButtons()
        : removeSaveButtons();
</script>

{#if window.location.href.includes("x.com") || window.location.href.includes("twitter.com")}
    <button
        class="cw-save-hide cw-tw-btn cw-save-btn cw-save-post-btn"
        title="Save as Snippet"
        bind:this={saveButton}
    >
        <div class="cw-tw-bg"></div>
        <div class="cw-save__loading-spinner" style="display: none;">
            <SVGIcon
                name="spinner"
                color={buttonColor}
                height="100%"
                size="100%"
                viewbox="0 0 24 24"
                strokeWidth="2"
            />
        </div>
        <div class="cw-save__icon">
            <SVGIcon
                name="save"
                size="20"
                color={buttonColor}
                viewbox="0 0 28 28"
                strokeWidth="0.2"
            />
        </div>
    </button>
{/if}

<style>
    @import "../../extension/external.css";
</style>
